//@ts-strict-ignore
import React, { FunctionComponent, useCallback, useContext, useState } from 'react';
import * as input from '../../components/connectedForm/inputComponents';
import { BlurTextField } from '../../components/connectedForm/inputComponents';
import getValue from '../../components/connectedForm/utils/getValue';
import { default as Form } from '../../components/connectedForm';
import { BlurTextFieldType } from '../../components/connectedForm/BlurTextField/BlurTextField';
import getClientMutationId from '../../utils/getClientMutationId';
import { IFormatterProps } from '../../@types/workspaceTypes';
import {
  IPurchaseTransaction,
  IPurchaseTransactionMutationResult,
  IPurchaseTransactionMutationVariables,
} from '../../graphql/schema/p4types';
import WorkspaceContext from '../../components/Context/WorkspaceContext';
import { useComponentQueryErrorHandling } from '../../utils/parseErrorResponse';
import { useMutation } from '@apollo/client';
import PurchaseTransaction from '../../graphql/entities/supplier/PurchaseTransaction';
import { BasicHeader } from '../../actions/BasicHeader';
import { ModalBottomButtonContainer } from '../../components/modals/Styles';
import { Button, SuccessButton } from '../../components/connectedForm/Button';
import FluentModal from '../../components/modals/FluentModal';

type IPurchaseTransactionNoteModalProps = {
  rowData?: IPurchaseTransaction;
  onUpdate?: Function;
  onClose?: Function;
};

const PurchaseTransactionNoteModalContent: FunctionComponent<IPurchaseTransactionNoteModalProps> = ({
  rowData,
  onClose,
}: IPurchaseTransactionNoteModalProps) => {
  const [note, setNote] = useState(rowData?.note);
  const [held, setHeld] = useState(rowData?.held);
  const { reloadData } = useContext(WorkspaceContext);

  const handleQueryError = useComponentQueryErrorHandling({ name: 'Note Update' });
  const [purchaseTransactionUpdate] = useMutation<
    IPurchaseTransactionMutationResult,
    IPurchaseTransactionMutationVariables
  >(PurchaseTransaction.update!, {
    onCompleted: () => {
      reloadData?.();
    },
    onError: handleQueryError,
  });

  const onSubmit = useCallback(() => {
    purchaseTransactionUpdate({
      variables: {
        input: {
          clientMutationId: getClientMutationId(),
          purchaseTransaction: {
            id: rowData?.id,
            concurrencyVersion: rowData?.concurrencyVersion,
            note,
            held,
          },
        },
      },
    });
    onClose?.();
  }, [held, note, onClose, purchaseTransactionUpdate, rowData?.concurrencyVersion, rowData?.id]);

  return (
    <div>
      <BasicHeader>Purchase Transaction</BasicHeader>
      <Form.Fieldset cols={1}>
        <Form.Legend label="Notes" />
        <input.Checkbox
          data-testid={'checkbox-held'}
          value={!!held}
          label={'Held'}
          onChange={(value) => setHeld(getValue(value))}
        />
        <BlurTextField
          data-testid={'text-field-note'}
          value={note}
          label={'Note'}
          onBlur={(value) => setNote(getValue(value))}
          type={BlurTextFieldType.ExpandableTextArea}
          style={{ minHeight: '200px', maxHeight: '200px', overflow: 'auto' }}
        />
      </Form.Fieldset>
      <ModalBottomButtonContainer>
        <Button onClick={onClose}>Cancel</Button>
        <SuccessButton onClick={onSubmit}>Update</SuccessButton>
      </ModalBottomButtonContainer>
    </div>
  );
};

const PurchaseTransactionNoteModalFormatter = (formatterProps: IFormatterProps<IPurchaseTransaction>) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <div onClick={() => setIsOpen(true)} style={{ width: '100%' }}>
        {formatterProps.value}
        <i className="popoverlink icon">more_vert</i>
      </div>
      <FluentModal isOpen={isOpen}>
        <div style={{ height: '440px', minWidth: '600px' }}>
          {isOpen ? (
            <PurchaseTransactionNoteModalContent
              rowData={formatterProps.dependentValues.rowData}
              onClose={() => setIsOpen(false)}
            />
          ) : null}
        </div>
      </FluentModal>
    </>
  );
};

export default PurchaseTransactionNoteModalFormatter;
